/**
 * Page 2 — Employees.
 *
 * Name (frozen), score, the seven scored metrics on a shaded band, three
 * muted context columns, and dollars against the title median.
 *
 * Each metric cell is the VALUE with its RANK underneath. No bars: fifteen cells each
 * carrying a value, a bar and a rank meant the bars were all you saw, and they only
 * restated the rank beneath them.
 *
 * Every column has a fixed width. Content-sized columns re-flow whenever the filter
 * changes, which makes the table feel unstable and moves a column out from under the
 * cursor between clicks.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useAppData } from '@/data/AppData'
import {
  availableMetrics,
  standing,
  personSubLine,
  type MetricDef,
  type Person,
  type Standing,
} from '@/data/selectors'
import {
  SCORE_MAX,
  SCORE_MIN,
  formatPoints,
  scoreBand,
  costAbovePeerAverage,
} from '@/measures/scorecard'
import { DataTable, type Column } from '@/components/Table'
import { LowVolumeTag, TerminatedChip, fmt } from '@/components/Bits'
import { ScoreTip } from '@/components/ScoreTip'
import { TrailingBadge, useTrailingCaption } from '@/components/TrailingScore'
import { money0, int, monthSpan } from '@/format'
import { rankState, rankText } from '@/measures/rankText'
import { copyRows, downloadCSV, type ExportColumn } from '@/ui/export'
import { define, withNote } from '@/measures/definitions'
import { PopOut } from '@/components/PopOut'

/** Widths chosen so the seven scored columns stay identical and the eye can scan down. */
const W = { person: 232, paylocity: 92, score: 82, metric: 98, context: 84, vsMedian: 132 }


/**
 * The Ledger's rank cell: green best third, red worst third, grey between; the "/n"
 * softened; the reading on hover. Unranked, the figure greyed over a bare dash.
 */
function MetricCell({ metric, st, period }: { metric: MetricDef; st: Standing; period: string }) {
  const v = fmt(metric.format, st.value)
  const hover = `${metric.label}: ${v} · ${rankText(st, period)}`
  if (st.rank === null)
    return (
      <div className="mc none" title={hover}>
        <span className="v">{v}</span>
        <span className="p">{rankState(st) === 'noData' ? 'no data' : '—'}</span>
      </div>
    )
  const cls = st.band === 'good' ? 'good' : st.band === 'crit' ? 'bad' : 'mid'
  return (
    <div
      className={`mc ${cls}`}
      title={hover}
    >
      <span className="v">{v}</span>
      <span className="p">
        {st.rank}
        <i>/{st.of}</i>
      </span>
    </div>
  )
}

export function Employees() {
  const { derived, scope, openPerson, coverage, trailing, label } = useAppData()
  /** The months every rank on the page covers, for its hover. */
  const period = monthSpan(derived.months)
  const trail = useTrailingCaption()
  // Metrics this bundle cannot populate are dropped, not dashed. Collection and the
  // 30-day recency are null on every one of the 509 people here; as columns they were
  // 1,018 cells of "— / no cohort" competing for the same eye movement as real ones.
  /** Everything the bundle can populate, in METRICS order — context columns faded, never hidden. */
  const ALL_SHOWN = useMemo(() => availableMetrics(coverage), [coverage])
  // Everyone in scope, claims or not. The Ledger lists only claim-carrying people and
  // this page once matched it, which hid staff who were paid and billed nothing —
  // exactly the people the unattributed-labor finding is about. The business owner
  // asked for all of them (2026-09-23). They sort last on every metric column, since
  // their rates are null, so the table still leads with where the money is.
  const rows = scope.people

  /*
   * Peer averages need every peer's stats, not just the ones on screen — the
   * comparison must not move when somebody narrows the filter bar. Active staff only.
   */
  const allStats = derived.peerStats

  /**
   * The Ledger's person cell: name and status chips, then the title, how many hold it,
   * and where they sit — shore and vendor.
   */
  const nameCell = (p: Person) => {
    const n = (scope.cohorts.get(p.jobTitle) ?? []).length
    return (
      <>
        <span className="nm">{p.employee}</span>
        {p.terminated && <> <TerminatedChip date={p.terminationDate} /></>}
        {p.management && <> <span className="pts-chip neut">mgmt</span></>}
        <br />
        <span className="sub2">{personSubLine(p, n)}</span>
      </>
    )
  }

  const columns = useMemo<Column<Person>[]>(() => {
    const cols: Column<Person>[] = [
      {
        key: 'person',
        header: 'Person',
        frozen: true,
        width: W.person,
        sort: (p) => p.employee,
        render: nameCell,
      },
      {
        // Review #373: the key other systems know the person by.
        key: 'paylocityId',
        header: 'Paylocity ID',
        dim: true,
        width: W.paylocity,
        sort: (p) => p.paylocityId,
        render: (p) => (
          <span className="mono" style={{ fontSize: 'var(--t-rank)' }}>
            {p.paylocityId ?? '—'}
          </span>
        ),
      },
      {
        key: 'score',
        // Read off the model, never typed. This said "Score / 9" for a day after the
        // scorecard moved to 11.5 — a hardcoded cap is a label that goes quietly wrong
        // the moment somebody approves another line.
        header: `Score / ${formatPoints(SCORE_MAX).replace('+', '')}`,
        help: define('score'),
        align: 'right',
        width: W.score,
        sort: (p) => (p.score.scored ? p.score.total : null),
        title: `Scorecard total, ${SCORE_MIN} to ${SCORE_MAX}. A dash means no attributed claims, or terminated: no score.`,
        render: (p) => (
          <ScoreTip score={p.score} who={`${p.department} · ${p.jobTitle}`}>
            {p.score.scored ? (
              <>
                <span className={`scbadge ${scoreBand(p.score.total)}`}>
                  {formatPoints(p.score.total)}
                </span>
                <LowVolumeTag lowVolume={p.score.lowVolume} />
              </>
            ) : (
              <span className="sub2">—</span>
            )}
          </ScoreTip>
        ),
      },
      {
        key: 'trailing',
        header: trail.label,
        title: trail.note,
        align: 'right',
        width: W.score,
        sort: (p) => {
          const t = trailing.byName.get(p.employee)
          return t?.scored ? t.total : null
        },
        render: (p) => (
          <TrailingBadge
            score={trailing.byName.get(p.employee)}
            who={`${p.department} · ${p.jobTitle}`}
          />
        ),
      },
    ]

    /*
     * ONE pass over METRICS, in METRICS order, so an unscored context column lands
     * beside the scored one it explains — billed total next to billed per month,
     * touches next to 0% touch share, the open pile next to how much was worked.
     *
     * They used to be two blocks: every scored metric, then every context metric
     * behind a divider. Reading a row meant jumping back and forth between the
     * number and the thing that accounts for it.
     */
    for (const metric of ALL_SHOWN) {
      if (metric.scored) {
        cols.push({
          key: metric.key,
          header: label(metric.short),
          align: 'right',
          band: true,
          width: W.metric,
          sort: (p) => metric.get(p),
          title: `${label(metric.label)} — ranked within the person's own job title`,
          help: withNote(define(metric.key), 'Ranked within the person’s own job title.'),
          render: (p) => (
            <MetricCell metric={metric} st={standing(scope.cohorts, p, metric)} period={period} />
          ),
        })
      } else {
        cols.push({
          key: metric.key,
          header: label(metric.short),
          align: 'right',
          dim: true,
          width: W.context,
          sort: (p) => metric.get(p),
          title: `${label(metric.label)} — context, not scored`,
          help: withNote(define(metric.key), 'Shown for context; it earns no points.'),
          render: (p) => (
            <MetricCell metric={metric} st={standing(scope.cohorts, p, metric)} period={period} />
          ),
        })
      }
    }

    cols.push({
      key: 'vsMedian',
      /*
       * Against the peer AVERAGE, on the scorecard's own peer group — department +
       * job title, shore-split for cost per claim.
       *
       * This was "$ vs title median": a different central tendency on a different
       * peer group from the score sitting two columns to its left. Two comparisons
       * on one row, disagreeing about who the peers are — the same defect the
       * supervisor cards had.
       */
      header: '$ vs peer average',
      help: define('vsPeer'),
      align: 'right',
      width: W.vsMedian,
      sort: (p) => costAbovePeerAverage(allStats, p.stats),
      title:
        'What this person’s distance from their job-title median costs across their claims',
      render: (p) => {
        const v = costAbovePeerAverage(allStats, p.stats)
        if (v === null) return <span className="sub2">—</span>
        // Exactly at the median is neither good nor bad — only a real gap gets a colour.
        const tone =
          Math.round(v) === 0
            ? 'var(--ink-2)'
            : v > 0
              ? 'var(--crit-ink)'
              : 'var(--good-ink)'
        return (
          <span className="mono" style={{ fontSize: 'var(--t-table)', color: tone }}>
            {v > 0 ? '+' : ''}
            {money0(v)}
          </span>
        )
      },
    })

    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derived, scope.cohorts, allStats, ALL_SHOWN, period, trailing, trail.note, label])

  /** Copy / CSV of exactly the rows and figures on screen. */
  const exportColumns: ExportColumn<Person>[] = [
    { header: 'Person', value: (p) => p.employee },
    { header: 'Paylocity ID', value: (p) => p.paylocityId ?? '' },
    { header: 'Job title', value: (p) => p.jobTitle },
    {
      header: `Score / ${SCORE_MAX}, ${period}`,
      value: (p) => (p.score.scored ? p.score.total : null),
    },
    {
      header: `${trail.label} score, ${trail.span}`,
      value: (p) => {
        const t = trailing.byName.get(p.employee)
        return t?.scored ? t.total : null
      },
    },
    ...ALL_SHOWN.map((m) => ({ header: m.short, value: (p: Person) => m.get(p) })),
    { header: '$ vs peer average', value: (p) => costAbovePeerAverage(allStats, p.stats) },
  ]
  /*
   * The Ledger's keyboard use: Tab reaches every header and row, Enter or Space sorts
   * a header, Enter opens a row. DataTable is shared and renders neither focusable, so
   * the page marks them itself and re-marks rows whenever the table re-renders them.
   */
  const tableRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = tableRef.current
    if (!el) return
    const mark = () =>
      el
        .querySelectorAll('th[data-sortable="true"], tr[data-clickable="true"]')
        .forEach((n) => n.getAttribute('tabindex') === '0' || n.setAttribute('tabindex', '0'))
    mark()
    const obs = new MutationObserver(mark)
    obs.observe(el, { childList: true, subtree: true })
    return () => obs.disconnect()
  }, [])
  const onTableKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement
    const sortable = t.matches('th[data-sortable="true"]') && (e.key === 'Enter' || e.key === ' ')
    const openable = t.matches('tr[data-clickable="true"]') && e.key === 'Enter'
    if (!sortable && !openable) return
    e.preventDefault()
    t.click()
  }

  /*
   * The Ledger starts every column high-to-low on its first click, names included (Z→A).
   * DataTable starts a left-aligned column A→Z, so an unsorted Person header gets a
   * second click straight after, once DataTable has taken the first.
   */
  const onTableClick = (e: MouseEvent<HTMLDivElement>) => {
    const th = (e.target as HTMLElement).closest('th')
    if (th && th.cellIndex === 0 && th.getAttribute('aria-sort') === 'none')
      setTimeout(() => th.click())
  }

  const [saveNote, setSaveNote] = useState<string | null>(null)
  const copy = async () => {
    const r = await copyRows(rows, exportColumns)
    setSaveNote(r === 'ok' ? 'Copied' : 'This view cannot use the clipboard.')
  }
  const csv = () => {
    const r = downloadCSV('every-person-measured-against-their-own-job-title.csv', rows, exportColumns)
    setSaveNote(r === 'ok' ? null : 'This view cannot start a download — an embedded preview blocks it.')
  }

  return (
    <>
      {/* The Ledger's .sec-head: a 1.5-line heading row, 4px above the Export row. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 18px',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <h1 className="page-title" style={{ lineHeight: 1.5 }}>
          Every person, measured against their own job title
        </h1>
        <span className="pts-chip neut">peer basis: job title</span>
      </div>
      {/* Which months the scores and ranks are for — the table itself never says. */}
      <p className="body-note" style={{ margin: '0 0 8px' }} data-testid="score-range">
        Scores and ranks for <b>{period}</b>.{' '}
        <span title={trail.note}>
          {trail.label} score: <b>{trail.span}</b>.
        </span>
      </p>

      <PopOut title="Every person, measured against their own job title" note="peer basis: job title">
        {({ button }) => (
          <>
            <div className="xbar" style={{ marginTop: 0 }}>
              {button}
              <span>Export</span>
              <button type="button" onClick={copy}>
                Copy
              </button>
              <button type="button" onClick={csv}>
                CSV
              </button>
              {saveNote && <em>{saveNote}</em>}
            </div>
            {/* The Ledger fades unscored columns whole, header included; DataTable only marks their cells. */}
            <style>
              {columns
                .map((c, i) => (c.dim ? `.emp-dense .dt tr > :nth-child(${i + 1})` : null))
                .filter(Boolean)
                .join(', ') + ' { opacity: 0.62; }'}
            </style>
            <div
              className="card emp-dense popout-fill"
              style={{ padding: 0, overflow: 'hidden' }}
              ref={tableRef}
              onKeyDown={onTableKey}
              onClick={onTableClick}
            >
              <DataTable
                storageKey="employees.people"
                rows={rows}
                columns={columns}
                rowKey={(p) => p.employee}
                onRowClick={(p) => openPerson(p.employee)}
                sortParam="sort"
                initialSort="vsMedian"
                initialDir="desc"
                maxHeight={560}
              />
            </div>

            <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 9 }}>
              {int(rows.length)} people shown
            </p>
          </>
        )}
      </PopOut>
    </>
  )
}
